/**
 * SuperMachinesTab — Commission machines and assign them to OEMs.
 * atoms_admin sees ALL machines across all orgs.
 * Navigates to full-page MachineEditPage wizard for create/edit.
 */
import React, { useEffect, useState } from 'react';
import {
  Stack, Group, Button, TextInput, Table, Badge, ActionIcon,
  Text, Loader, Alert, Tooltip,
} from '@mantine/core';
import {
  MdEdit, MdAddCircleOutline, MdSearch, MdRefresh,
} from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { getMachines } from '../../API/adminAPI';

const ATOMS_BLUE = '#1F4E79';

const SuperMachinesTab = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const md = await getMachines();
      setMachines(md.machines || []);
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = machines.filter((m) =>
    m.machine_id?.toLowerCase().includes(search.toLowerCase()) ||
    m.machine_name?.toLowerCase().includes(search.toLowerCase()) ||
    (m.owned_by || '').toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => navigate('/app/machines/new');
  const openEdit = (m) => navigate(`/app/machines/${m.id}/edit`, { state: { machine: m } });

  if (loading) return <Loader mt="xl" mx="auto" />;

  return (
    <Stack>
      {error && <Alert color="red">{error}</Alert>}

      <Group position="apart">
        <TextInput
          placeholder="Search by ID, name or owner…"
          icon={<MdSearch />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          sx={{ flex: 1, maxWidth: 380 }}
        />
        <Group spacing="xs">
          <ActionIcon onClick={load} variant="subtle"><MdRefresh size={20} /></ActionIcon>
          <Button
            leftIcon={<MdAddCircleOutline />}
            onClick={openCreate}
            sx={{ backgroundColor: ATOMS_BLUE, '&:hover': { backgroundColor: '#163a5f' } }}
          >
            Commission Machine
          </Button>
        </Group>
      </Group>

      <Table striped highlightOnHover>
        <thead>
          <tr>
            <th>Machine ID</th>
            <th>Name</th>
            <th>OEM Owner</th>
            <th>Timezone</th>
            <th style={{ width: 60 }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr><td colSpan={5}>
              <Text color="dimmed" align="center" py="md">No machines found</Text>
            </td></tr>
          ) : filtered.map((m) => (
            <tr key={m.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(m)}>
              <td>
                <Badge variant="outline" color="blue" size="sm">{m.machine_id}</Badge>
              </td>
              <td><Text size="sm" weight={500}>{m.machine_name || '—'}</Text></td>
              <td>
                {m.owned_by ? (
                  <Badge variant="light" color="blue" size="sm">{m.owned_by}</Badge>
                ) : (
                  <Text size="xs" color="red">⚠ Unassigned</Text>
                )}
              </td>
              <td>
                <Text size="xs" color="dimmed">
                  UTC{m.timezone_offset_minutes >= 0 ? '+' : ''}{m.timezone_offset_minutes / 60}h
                </Text>
              </td>
              <td>
                <Tooltip label="Edit">
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={(e) => { e.stopPropagation(); openEdit(m); }}
                  >
                    <MdEdit />
                  </ActionIcon>
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Stack>
  );
};

export default SuperMachinesTab;
